import {
    AdminLandingPageLink,
    AdminShopifyProduct,
    AdminShopifyCollection,
    AdminShopifyProductVariant,
    AdminShopifyProductImage,
    VLPVariant,
    AdminVirtualProduct,
} from '@storefronts/common-types'
import {
    useState,
    memo,
    useMemo,
    useContext,
    useCallback,
    type ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import {
    useSensors,
    useSensor,
    PointerSensor,
    DndContext,
    closestCenter,
    DragEndEvent,
    DragStartEvent,
} from '@dnd-kit/core'
import {
    useSortable,
    arrayMove,
    SortableContext,
    rectSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Icon } from '../Icon'
import { DragOverlay } from '@dnd-kit/core'
import { R } from '../../modules/api/actions'
import Image from 'next/image'
import { DebounceSearchBar } from '../DebounceSearchBar'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { toast } from '@/components/ui/sonner'
import { cn } from '@/lib/utils'
import { LoadingSpinner } from '@/components/ui/spinner'
import { H2, Muted, Small, XSmall, Medium } from '@/components/ui/typography'
import { LandingPageEditorOverrideContext } from '../landingpages/LandingPageOverrideContextProvider'
import { NoResultsMessage } from './NoResultsMessage'
import { shouldShowSelectedProductDefaultVariantLine } from './storefront-default-variant/model'
import { SelectedProductDefaultVariantLine } from './storefront-default-variant/StorefrontDefaultVariantComponents'
import { LoadingMessage } from '@/components/shared/LoadingMessage'
import pLimit from 'p-limit'
import {
    compareByQuantityOrderedDesc,
    formatLastOrderedLabel,
} from './formatOrderData'

export enum LandingPageLinkTab {
    PRODUCTS = 'products',
    COLLECTIONS = 'collections',
    STOREFRONT = 'storefront',
    VIRTUAL_PRODUCTS = 'virtual-products',
}

interface SelectLandingPageLinksProps {
    selectedLinks: AdminLandingPageLink[]
    setSelectedLinks: React.Dispatch<
        React.SetStateAction<AdminLandingPageLink[]>
    >
    ignoredShopifyIds?: string[]
    showNotes?: boolean
    showFeatureBadge?: boolean
    showStorefrontTab?: boolean
    noProductsSuggestion?: string
    /** If provided, called before removing a link. Return false to cancel removal (e.g. after user cancels a confirmation). */
    onBeforeRemove?: (link: AdminLandingPageLink) => Promise<boolean>
    /**
     * When true, selected-product rows show storefront default variant override UI.
     * Use for main landing page product list only (not PLP grid or cart upsells).
     */
    showStorefrontDefaultVariantUI?: boolean
}

type ProductWithVirtualInfo = AdminShopifyProduct & {
    virtualProductId?: number
    attachedProducts?: Array<{ id: string; name: string | null }>
    defaultShopifyProductId?: string | null
    quantityOrdered?: number | null
    lastOrderDate?: Date | string | null
}

// eslint-disable-next-line complexity
export const SelectLandingPageLinks: React.FC<SelectLandingPageLinksProps> = ({
    selectedLinks,
    setSelectedLinks,
    ignoredShopifyIds = [],
    showNotes = false,
    showFeatureBadge = true,
    showStorefrontTab = false,
    noProductsSuggestion,
    onBeforeRemove,
    showStorefrontDefaultVariantUI = false,
}) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [activeTab, setActiveTab] = useState<LandingPageLinkTab>(
        LandingPageLinkTab.PRODUCTS,
    )

    // Shared virtual products data - fetched once and shared across all panels
    const { data: virtualProducts = [], isLoading: virtualProductsLoading } =
        useQuery({
            queryKey: ['virtual-products', true],
            queryFn: () => R.getAllVirtualProducts(true),
        })
    const hasVirtualProducts = virtualProducts.length > 0

    const onRemove = async (link: AdminLandingPageLink) => {
        if (onBeforeRemove) {
            const ok = await onBeforeRemove(link)
            if (!ok) return
        }
        setSelectedLinks((prev) =>
            prev.filter((l) => l.linkHash !== link.linkHash),
        )
    }

    const applyLandingPageLinkUpdate = useCallback(
        (linkHash: string, next: AdminLandingPageLink) => {
            setSelectedLinks((prev) =>
                prev.map((l) => (l.linkHash === linkHash ? next : l)),
            )
        },
        [setSelectedLinks],
    )

    const onSelect = async (products: ProductWithVirtualInfo[]) => {
        const selectedShopifyIds = selectedLinks.map((l) => l.shopifyId)
        const selectedVirtualProductIds = new Set(
            selectedLinks
                .filter((l) => l.virtualProductId)
                .map((l) => l.virtualProductId!),
        )
        const eligibleProducts = products.filter((product) => {
            // For virtual products, check virtualProductId
            if (product.virtualProductId) {
                return !selectedVirtualProductIds.has(product.virtualProductId)
            }
            // For regular products, check shopifyId
            return (
                !selectedShopifyIds.includes(product.shopifyId) &&
                !ignoredShopifyIds.includes(product.shopifyId)
            )
        })

        if (eligibleProducts.length === 0) {
            return
        }

        const landingPageLinks: AdminLandingPageLink[] = []
        // Limit concurrent requests to avoid overwhelming the server
        const limit = pLimit(5)
        const results = await Promise.allSettled(
            eligibleProducts.map((product) =>
                limit(async () => {
                    if (product.virtualProductId) {
                        return R.getLandingPageLinkByVirtualProductId(
                            product.virtualProductId,
                        )
                    }
                    return product.linkHash
                        ? R.getLandingPageLink(product.linkHash)
                        : R.importShopifyProduct(product.shopifyId)
                }),
            ),
        )

        const failedProducts: ProductWithVirtualInfo[] = []
        results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
                landingPageLinks.push(result.value.landingPageLink)
            } else {
                failedProducts.push(eligibleProducts[index])
                console.error(
                    `Failed to add product ${eligibleProducts[index]?.shopifyId || 'unknown'}`,
                    JSON.stringify(result.reason),
                )
            }
        })

        if (failedProducts.length > 0) {
            toast.error(`Failed to add ${failedProducts.length} product(s)`)
        }

        if (landingPageLinks.length === 0) {
            return
        }

        setSelectedLinks((prev) => [...prev, ...landingPageLinks])
    }

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 10,
            },
        }),
    )

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event
        if (over && active.id !== over.id) {
            setSelectedLinks((items) => {
                const oldIndex = items.findIndex(
                    (item) => item.linkHash === active.id,
                )
                const newIndex = items.findIndex(
                    (item) => item.linkHash === over.id,
                )
                return arrayMove(items, oldIndex, newIndex)
            })
        }
        setActiveId(null)
    }

    const [activeId, setActiveId] = useState<string | null>(null)

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id)
    }

    const activeDragLink = activeId
        ? selectedLinks.find((link) => link.linkHash === activeId)
        : undefined

    const activeDragLinkIsFeatured =
        !!activeDragLink &&
        showFeatureBadge &&
        selectedLinks[0]?.linkHash === activeDragLink.linkHash

    const virtualProductDefaultShopifyIdById = new Map(
        virtualProducts.map((vp) => [vp.id, vp.defaultShopifyProductId]),
    )

    const featureMessage = showFeatureBadge
        ? 'The first product will be used as the featured product within the Storefront. It will be highlighted below the shoppable video on mobile & desktop, and on the left hand side of other storefronts on desktop.'
        : ''

    return (
        <div className="size-full px-6 py-3">
            <div className="flex h-full gap-6">
                <div className="flex h-full flex-1 flex-col overflow-hidden">
                    <div className="shrink-0">
                        <div className="mb-4 flex gap-2">
                            <Button
                                onClick={() =>
                                    setActiveTab(LandingPageLinkTab.PRODUCTS)
                                }
                                variant={
                                    activeTab === LandingPageLinkTab.PRODUCTS
                                        ? 'default'
                                        : 'secondary'
                                }
                                size="sm"
                            >
                                Products
                            </Button>
                            <Button
                                onClick={() =>
                                    setActiveTab(LandingPageLinkTab.COLLECTIONS)
                                }
                                variant={
                                    activeTab === LandingPageLinkTab.COLLECTIONS
                                        ? 'default'
                                        : 'secondary'
                                }
                                size="sm"
                            >
                                Collections
                            </Button>
                            {showStorefrontTab && (
                                <Button
                                    onClick={() =>
                                        setActiveTab(
                                            LandingPageLinkTab.STOREFRONT,
                                        )
                                    }
                                    variant={
                                        activeTab ===
                                        LandingPageLinkTab.STOREFRONT
                                            ? 'default'
                                            : 'secondary'
                                    }
                                    size="sm"
                                >
                                    Storefront
                                </Button>
                            )}
                            {hasVirtualProducts && (
                                <Button
                                    onClick={() =>
                                        setActiveTab(
                                            LandingPageLinkTab.VIRTUAL_PRODUCTS,
                                        )
                                    }
                                    variant={
                                        activeTab ===
                                        LandingPageLinkTab.VIRTUAL_PRODUCTS
                                            ? 'default'
                                            : 'secondary'
                                    }
                                    size="sm"
                                >
                                    Virtual Products
                                </Button>
                            )}
                        </div>
                        <div>
                            <DebounceSearchBar
                                placeholder={
                                    activeTab === LandingPageLinkTab.PRODUCTS ||
                                    activeTab ===
                                        LandingPageLinkTab.STOREFRONT ||
                                    activeTab ===
                                        LandingPageLinkTab.VIRTUAL_PRODUCTS
                                        ? 'Search by product name or ID...'
                                        : 'Search by collection name, template name or ID....'
                                }
                                callbackFn={setSearchTerm}
                            />
                        </div>
                    </div>
                    <AvailableProductsPanel
                        searchTerm={searchTerm}
                        selectedLinks={selectedLinks}
                        ignoredShopifyIds={ignoredShopifyIds}
                        onSelect={onSelect}
                        virtualProducts={virtualProducts}
                        virtualProductsLoading={virtualProductsLoading}
                        className={cn(
                            activeTab !== LandingPageLinkTab.PRODUCTS &&
                                'hidden',
                        )}
                    />
                    <CollectionsPanel
                        searchTerm={searchTerm}
                        selectedLinks={selectedLinks}
                        setSelectedLinks={setSelectedLinks}
                        ignoredShopifyIds={ignoredShopifyIds}
                        onSelect={onSelect}
                        className={cn(
                            activeTab !== LandingPageLinkTab.COLLECTIONS &&
                                'hidden',
                        )}
                    />
                    <StorefrontProductsPanel
                        searchTerm={searchTerm}
                        selectedLinks={selectedLinks}
                        ignoredShopifyIds={ignoredShopifyIds}
                        onSelect={onSelect}
                        virtualProducts={virtualProducts}
                        className={cn(
                            activeTab !== LandingPageLinkTab.STOREFRONT &&
                                'hidden',
                        )}
                    />
                    <VirtualProductsPanel
                        searchTerm={searchTerm}
                        selectedLinks={selectedLinks}
                        onSelect={onSelect}
                        virtualProducts={virtualProducts}
                        virtualProductsLoading={virtualProductsLoading}
                        className={cn(
                            activeTab !== LandingPageLinkTab.VIRTUAL_PRODUCTS &&
                                'hidden',
                        )}
                    />
                </div>
                <Card className="flex h-full flex-1 flex-col overflow-hidden p-6 shadow-sm">
                    <div className="shrink-0">
                        <div className="mb-4 flex items-center justify-between gap-4">
                            <H2>Selected Products ({selectedLinks.length})</H2>
                            <Button
                                onClick={() => setSelectedLinks([])}
                                disabled={selectedLinks.length === 0}
                                size="sm"
                                variant="destructive"
                            >
                                Remove All
                            </Button>
                        </div>
                        {showNotes && (
                            <div className="mb-6">
                                <Medium className="mb-2">Notes for AI</Medium>
                                <Textarea
                                    placeholder="Add any specific instructions about these products..."
                                    rows={3}
                                />
                            </div>
                        )}
                    </div>
                    <div className="flex-1 overflow-y-auto pr-4">
                        {selectedLinks.length > 0 && (
                            <Medium className="mb-3">
                                Drag to reorder products. {featureMessage}
                            </Medium>
                        )}
                        {selectedLinks.length === 0 ? (
                            <NoResultsMessage
                                message="No products currently selected."
                                suggestion={noProductsSuggestion}
                            />
                        ) : (
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragStart={handleDragStart}
                                onDragEnd={handleDragEnd}
                            >
                                <div className="divide-y overflow-hidden rounded-lg border divide-border border-border">
                                    <SortableContext
                                        items={selectedLinks.map(
                                            (link) => link.linkHash,
                                        )}
                                        strategy={rectSortingStrategy}
                                    >
                                        {selectedLinks.map((link, index) => (
                                            <ReorderLinkThumbnail
                                                key={link.linkHash}
                                                link={link}
                                                displayShopifyId={getLinkDisplayShopifyId(
                                                    link,
                                                    virtualProductDefaultShopifyIdById,
                                                )}
                                                onRemove={onRemove}
                                                onApplyLinkUpdate={
                                                    applyLandingPageLinkUpdate
                                                }
                                                showStorefrontDefaultVariantUI={
                                                    showStorefrontDefaultVariantUI
                                                }
                                                isFeatured={
                                                    index === 0 &&
                                                    showFeatureBadge
                                                }
                                            />
                                        ))}
                                    </SortableContext>
                                </div>
                                <DragOverlay>
                                    {activeDragLink ? (
                                        <div className="pointer-events-none min-w-[min(100%,28rem)]">
                                            <SelectedProductPickerRow
                                                link={activeDragLink}
                                                displayShopifyId={getLinkDisplayShopifyId(
                                                    activeDragLink,
                                                    virtualProductDefaultShopifyIdById,
                                                )}
                                                isFeatured={
                                                    activeDragLinkIsFeatured
                                                }
                                                isDragOverlay
                                            />
                                        </div>
                                    ) : null}
                                </DragOverlay>
                            </DndContext>
                        )}
                    </div>
                </Card>
            </div>
        </div>
    )
}

interface AvailableProductsPanelProps {
    searchTerm: string
    selectedLinks: AdminLandingPageLink[]
    ignoredShopifyIds: string[]
    onSelect: (products: ProductWithVirtualInfo[]) => Promise<void>
    virtualProducts: AdminVirtualProduct[]
    virtualProductsLoading: boolean
    className?: string
}

function filterSelectedOrIgnoredItems<
    T extends AdminShopifyProduct | AdminLandingPageLink,
>(
    items: T[],
    selectedLinks: AdminLandingPageLink[],
    ignoredShopifyIds: string[],
): T[] {
    const shopifyIdsToIgnore = new Set([
        ...selectedLinks.map((l) => l.shopifyId),
        ...ignoredShopifyIds,
    ])

    return items.filter(
        (p) => !!p.shopifyId && !shopifyIdsToIgnore.has(p.shopifyId),
    )
}

const SEARCH_SUGGESTION = 'Try searching with a different term.'
const PICKER_PAGE_SIZE = 500

async function fetchAllShopifyProducts(options: {
    searchText: string
    excludedShopifyIds: string[]
}): Promise<AdminShopifyProduct[]> {
    const products: AdminShopifyProduct[] = []
    let page = 1

    while (true) {
        const { products: pageProducts, meta } = await R.getAllShopifyProducts({
            size: PICKER_PAGE_SIZE,
            page,
            searchText: options.searchText,
            excludedShopifyIds: options.excludedShopifyIds,
        })
        products.push(...pageProducts)
        if (pageProducts.length === 0 || meta?.next == null) break
        page = meta.next
    }

    return products
}

async function fetchAllShopifyCollections(
    searchText: string,
): Promise<AdminShopifyCollection[]> {
    const collections: AdminShopifyCollection[] = []
    let page = 1

    while (true) {
        const { collections: pageCollections, meta } =
            await R.getAllShopifyCollections({
                size: PICKER_PAGE_SIZE,
                page,
                searchText,
                includeEmptyCollections: true,
            })
        collections.push(...pageCollections)
        if (pageCollections.length === 0 || meta?.next == null) break
        page = meta.next
    }

    return collections
}

/** Return both GID and numeric forms of a Shopify product ID for set membership. */
function toBothIdFormats(id: string): [string, string] {
    if (id.startsWith('gid://')) {
        const numeric = id.replace(/.*\//, '')
        return [id, numeric]
    }
    return [id, `gid://shopify/Product/${id}`]
}

function convertVirtualProductToProductWithVirtualInfo(
    virtualProduct: AdminVirtualProduct,
): ProductWithVirtualInfo {
    const firstProduct = virtualProduct.attachedProducts[0]
    const shopifyId = firstProduct?.id || ''

    return {
        shopifyId,
        title: virtualProduct.name,
        handle: '',
        variants: [],
        images: virtualProduct.imageUrl
            ? [{ src: virtualProduct.imageUrl }]
            : [],
        virtualProductId: virtualProduct.id,
        attachedProducts: virtualProduct.attachedProducts,
        defaultShopifyProductId: virtualProduct.defaultShopifyProductId,
        quantityOrdered: virtualProduct.quantityOrdered,
        lastOrderDate: virtualProduct.lastOrderDate,
    }
}

// Helper function to filter and convert virtual products
function filterVirtualProducts(
    virtualProducts: AdminVirtualProduct[],
    searchTerm: string,
    selectedLinks: AdminLandingPageLink[],
    replacementOnly?: boolean,
): ProductWithVirtualInfo[] {
    const sanitizedSearchTerm = searchTerm?.length
        ? searchTerm.toLowerCase()
        : undefined
    const selectedVirtualProductIds = new Set(
        selectedLinks
            .filter((link) => link.virtualProductId)
            .map((link) => link.virtualProductId!),
    )

    return virtualProducts
        .filter((vp) => {
            if (replacementOnly && !vp.replaceNonVirtualProducts) return false
            if (!sanitizedSearchTerm) return true
            return (
                vp.name.toLowerCase().includes(sanitizedSearchTerm) ||
                vp.id.toString().includes(sanitizedSearchTerm) ||
                vp.attachedProducts.some(
                    (p) =>
                        p.id.toLowerCase().includes(sanitizedSearchTerm) ||
                        p.name?.toLowerCase().includes(sanitizedSearchTerm),
                )
            )
        })
        .map(convertVirtualProductToProductWithVirtualInfo)
        .filter((product) => {
            if (product.virtualProductId) {
                return !selectedVirtualProductIds.has(product.virtualProductId)
            }
            return false
        })
}

const AvailableProductsPanel: React.FC<AvailableProductsPanelProps> = ({
    searchTerm,
    selectedLinks,
    ignoredShopifyIds,
    onSelect,
    virtualProducts,
    virtualProductsLoading,
    className,
}) => {
    const ignoredShopifyIdsKey = ignoredShopifyIds.join(',')
    const excludedShopifyIds = useMemo(
        () =>
            ignoredShopifyIdsKey
                .split(',')
                .filter((id) => !!id)
                .map((id) =>
                    id.startsWith('gid://')
                        ? id
                        : `gid://shopify/Product/${id}`,
                ),
        [ignoredShopifyIdsKey],
    )

    const { data: allShopifyProducts = [], isLoading } = useQuery({
        queryKey: ['shopify-products-picker', searchTerm, ignoredShopifyIdsKey],
        queryFn: () =>
            fetchAllShopifyProducts({
                searchText: searchTerm,
                excludedShopifyIds,
            }),
    })

    const selectableShopifyProducts = useMemo(() => {
        const filteredVirtualProducts = filterVirtualProducts(
            virtualProducts,
            searchTerm,
            selectedLinks,
            true,
        )

        const shopifyIdsWithReplacement = new Set<string>()
        filteredVirtualProducts.forEach((p) => {
            const id = p.defaultShopifyProductId
            if (id)
                toBothIdFormats(id).forEach((form) =>
                    shopifyIdsWithReplacement.add(form),
                )
        })

        const regularProducts = filterSelectedOrIgnoredItems(
            allShopifyProducts,
            selectedLinks,
            ignoredShopifyIds,
        ).filter((p) => !shopifyIdsWithReplacement.has(p.shopifyId))

        return [...regularProducts, ...filteredVirtualProducts].sort(
            compareByQuantityOrderedDesc,
        )
    }, [
        allShopifyProducts,
        virtualProducts,
        selectedLinks,
        ignoredShopifyIds,
        searchTerm,
    ])

    if (isLoading || virtualProductsLoading) {
        return (
            <LoadingMessage
                message="Loading products..."
                className={className}
            />
        )
    }

    return (
        <div className={cn('flex-1 overflow-y-auto pr-4', className)}>
            <div className="divide-y overflow-hidden rounded-lg border divide-border border-border">
                {selectableShopifyProducts.length === 0 ? (
                    <NoResultsMessage
                        message="No products found"
                        searchTerm={searchTerm}
                        suggestion={
                            searchTerm?.length ? SEARCH_SUGGESTION : undefined
                        }
                    />
                ) : (
                    selectableShopifyProducts.map((product) => (
                        <AddShopifyProductThumbnail
                            key={
                                product.virtualProductId
                                    ? `virtual-${product.virtualProductId}`
                                    : `regular-${product.shopifyId}`
                            }
                            product={product}
                            onSelect={onSelect}
                        />
                    ))
                )}
            </div>
        </div>
    )
}

interface StorefrontProductsPanelProps {
    searchTerm: string
    selectedLinks: AdminLandingPageLink[]
    ignoredShopifyIds: string[]
    onSelect: (products: ProductWithVirtualInfo[]) => Promise<void>
    virtualProducts: AdminVirtualProduct[]
    className?: string
}

// Creating a temporary placeholder from the AdminLandingPageLink to display a product in the UI.
// Doesn't make sense to fetch the product only to display the same information and
// then return a landingPageLink back if the product is selected.
function convertAdminLandingPageLinkToAdminShopifyProduct(
    link: AdminLandingPageLink,
): ProductWithVirtualInfo {
    // Convert VLPVariant[] to AdminShopifyProductVariant[]
    // No need to go too deep here as the variants aren't really shown in the UI.
    const variants: AdminShopifyProductVariant[] =
        link.variantInfo?.map((variant: VLPVariant) => ({
            shopifyId: variant.shopifyId,
            title: variant.title,
            price: {
                price: variant.price.price.toString(),
                compareAtPrice: variant.price.compareAtPrice?.toString(),
            },
            sellableOnlineQuantity: variant.available ? '999' : '0', // Approximate qty for legacy UI
            available: variant.available,
        })) ?? []

    // Convert imageUrl and carouselImages to AdminShopifyProductImage[]
    const images: AdminShopifyProductImage[] = [
        { src: link.imageUrl },
        ...(link.carouselImages ?? []).map((url: string) => ({ src: url })),
    ]

    return {
        shopifyId: link.shopifyId,
        title: link.title,
        handle: '', // not used for anything in the UI
        variants,
        images,
        linkHash: link.linkHash,
        virtualProductId: link.virtualProductId ?? undefined,
        quantityOrdered: link.quantityOrdered,
        lastOrderDate: link.lastOrderDate,
        // attachedProducts not available from ApiLandingPageLink, will be undefined
    }
}

const StorefrontProductsPanel: React.FC<StorefrontProductsPanelProps> = ({
    searchTerm,
    selectedLinks,
    ignoredShopifyIds,
    onSelect,
    virtualProducts,
    className,
}) => {
    const { stagedLandingPage } = useContext(LandingPageEditorOverrideContext)
    const [isAddingAll, setIsAddingAll] = useState(false)

    // Build virtual products map from shared virtual products data
    const virtualProductsMap = useMemo(() => {
        const map = new Map<
            number,
            Array<{ id: string; name: string | null }>
        >()
        virtualProducts.forEach((vp) => {
            map.set(vp.id, vp.attachedProducts)
        })
        return map
    }, [virtualProducts])

    // Build defaultShopifyProductId map for virtual products
    const virtualProductDefaultMap = useMemo(() => {
        const map = new Map<number, string | null>()
        virtualProducts.forEach((vp) => {
            map.set(vp.id, vp.defaultShopifyProductId)
        })
        return map
    }, [virtualProducts])

    const allStorefrontProducts = useMemo(() => {
        return (
            [...(stagedLandingPage?.landingPageLinks ?? [])]
                .map(convertAdminLandingPageLinkToAdminShopifyProduct)
                .map((product) => {
                    // Enrich virtual products with attachedProducts and defaultShopifyProductId
                    if (product.virtualProductId) {
                        const attachedProducts = virtualProductsMap.get(
                            product.virtualProductId,
                        )
                        const defaultShopifyProductId =
                            virtualProductDefaultMap.get(
                                product.virtualProductId,
                            )
                        if (attachedProducts) {
                            return {
                                ...product,
                                attachedProducts,
                                defaultShopifyProductId,
                            }
                        }
                    }
                    return product
                })
                // Make sure we don't have duplicate products under different LandingPageLinks.
                // For virtual products, use virtualProductId; for regular products, use shopifyId
                .filter(
                    (currentProduct, index, self) =>
                        self.findIndex((productToCompare) => {
                            if (currentProduct.virtualProductId) {
                                return (
                                    productToCompare.virtualProductId ===
                                    currentProduct.virtualProductId
                                )
                            }
                            if (productToCompare.virtualProductId) {
                                return false // Virtual products are never duplicates of regular products
                            }
                            return (
                                productToCompare.shopifyId ===
                                currentProduct.shopifyId
                            )
                        }) === index,
                )
        )
    }, [
        stagedLandingPage?.landingPageLinks,
        virtualProductsMap,
        virtualProductDefaultMap,
    ])

    const selectableStorefrontProducts = useMemo(() => {
        const sanitizedSearchTerm = searchTerm?.length
            ? searchTerm.toLowerCase()
            : undefined
        return filterSelectedOrIgnoredItems(
            allStorefrontProducts,
            selectedLinks,
            ignoredShopifyIds,
        ).filter(
            (p) =>
                !sanitizedSearchTerm ||
                p.title?.toLowerCase().includes(sanitizedSearchTerm) ||
                p.shopifyId.toLowerCase().includes(sanitizedSearchTerm),
        )
    }, [allStorefrontProducts, selectedLinks, ignoredShopifyIds, searchTerm])

    const handleAddAll = async () => {
        setIsAddingAll(true)
        try {
            await onSelect(selectableStorefrontProducts)
        } catch (error) {
            console.error(error)
            toast.error('Unable to add all products. Please try again.')
        } finally {
            setIsAddingAll(false)
        }
    }

    return (
        <div className={cn('flex-1 overflow-y-auto pr-4', className)}>
            {selectableStorefrontProducts.length > 0 && (
                <div className="mb-3 flex justify-end">
                    <Button
                        onClick={handleAddAll}
                        disabled={isAddingAll}
                        size="sm"
                        variant="default"
                        aria-label="Add all products"
                    >
                        {isAddingAll ? (
                            <>
                                <LoadingSpinner className="mr-2 size-4" />
                                Adding...
                            </>
                        ) : (
                            'Add all'
                        )}
                    </Button>
                </div>
            )}
            <div className="divide-y overflow-hidden rounded-lg border divide-border border-border">
                {selectableStorefrontProducts.length === 0 ? (
                    <NoResultsMessage
                        message="No products found"
                        searchTerm={searchTerm}
                        suggestion={
                            searchTerm?.length ? SEARCH_SUGGESTION : undefined
                        }
                    />
                ) : (
                    selectableStorefrontProducts.map((product) => (
                        <AddShopifyProductThumbnail
                            key={
                                product.virtualProductId
                                    ? `virtual-${product.virtualProductId}`
                                    : `regular-${product.shopifyId}`
                            }
                            product={product}
                            onSelect={onSelect}
                            isDisabled={isAddingAll}
                        />
                    ))
                )}
            </div>
        </div>
    )
}

interface CollectionsPanelProps {
    searchTerm: string
    selectedLinks: AdminLandingPageLink[]
    setSelectedLinks: React.Dispatch<
        React.SetStateAction<AdminLandingPageLink[]>
    >
    ignoredShopifyIds: string[]
    onSelect: (products: ProductWithVirtualInfo[]) => Promise<void>
    className?: string
}

const CollectionsPanel: React.FC<CollectionsPanelProps> = ({
    searchTerm,
    selectedLinks,
    setSelectedLinks,
    ignoredShopifyIds,
    onSelect,
    className,
}) => {
    const { data: collections = [], isLoading } = useQuery({
        queryKey: ['shopify-collections-picker', searchTerm],
        queryFn: () => fetchAllShopifyCollections(searchTerm),
    })

    const sortedCollections = useMemo(
        () =>
            [...collections].sort(
                (a, b) =>
                    (a.title ?? '').localeCompare(b.title ?? '') ||
                    a.shopifyId.localeCompare(b.shopifyId),
            ),
        [collections],
    )

    if (isLoading) {
        return (
            <LoadingMessage
                message="Loading collections..."
                className={className}
            />
        )
    }

    return (
        <div className={cn('flex-1 overflow-y-auto pr-4', className)}>
            <div className="flex flex-col gap-3">
                {sortedCollections.length === 0 ? (
                    <NoResultsMessage
                        message="No collections found"
                        searchTerm={searchTerm}
                        suggestion={
                            searchTerm?.length ? SEARCH_SUGGESTION : undefined
                        }
                    />
                ) : (
                    sortedCollections.map((collection) => (
                        <CollectionListItem
                            key={collection.shopifyId}
                            collection={collection}
                            selectedLinks={selectedLinks}
                            setSelectedLinks={setSelectedLinks}
                            ignoredShopifyIds={ignoredShopifyIds}
                            onSelect={onSelect}
                        />
                    ))
                )}
            </div>
        </div>
    )
}

interface VirtualProductsPanelProps {
    searchTerm: string
    selectedLinks: AdminLandingPageLink[]
    onSelect: (products: ProductWithVirtualInfo[]) => Promise<void>
    virtualProducts: AdminVirtualProduct[]
    virtualProductsLoading: boolean
    className?: string
}

const VirtualProductsPanel: React.FC<VirtualProductsPanelProps> = ({
    searchTerm,
    selectedLinks,
    onSelect,
    virtualProducts,
    virtualProductsLoading,
    className,
}) => {
    const selectableProducts = useMemo(() => {
        return filterVirtualProducts(virtualProducts, searchTerm, selectedLinks)
    }, [virtualProducts, selectedLinks, searchTerm])

    if (virtualProductsLoading) {
        return (
            <LoadingMessage
                message="Loading virtual products..."
                className={className}
            />
        )
    }

    return (
        <div className={cn('flex-1 overflow-y-auto pr-4', className)}>
            <div className="divide-y overflow-hidden rounded-lg border divide-border border-border">
                {selectableProducts.length === 0 ? (
                    <NoResultsMessage
                        message="No virtual products found"
                        searchTerm={searchTerm}
                        suggestion={
                            searchTerm?.length ? SEARCH_SUGGESTION : undefined
                        }
                    />
                ) : (
                    selectableProducts.map((product) => (
                        <AddShopifyProductThumbnail
                            key={`virtual-${product.virtualProductId}`}
                            product={product}
                            onSelect={onSelect}
                        />
                    ))
                )}
            </div>
        </div>
    )
}

interface CollectionListItemProps {
    collection: AdminShopifyCollection
    selectedLinks: AdminLandingPageLink[]
    setSelectedLinks: React.Dispatch<
        React.SetStateAction<AdminLandingPageLink[]>
    >
    ignoredShopifyIds: string[]
    onSelect: (products: AdminShopifyProduct[]) => Promise<void>
}

const CollectionListItem: React.FC<CollectionListItemProps> = ({
    collection,
    selectedLinks,
    setSelectedLinks,
    ignoredShopifyIds,
    onSelect,
}) => {
    const [isExpanded, setIsExpanded] = useState(false)
    const [isAddingAll, setIsAddingAll] = useState(false)

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded)
    }

    const validProducts = useMemo(
        () =>
            [
                ...filterSelectedOrIgnoredItems(
                    collection.products ?? [],
                    [],
                    ignoredShopifyIds,
                ),
            ].sort(compareByQuantityOrderedDesc),
        [collection.products, ignoredShopifyIds],
    )

    const handleAddAll = async (e: React.MouseEvent) => {
        e.stopPropagation()
        setIsAddingAll(true)

        try {
            const response = await R.importShopifyCollection(
                collection.shopifyId,
            )
            setSelectedLinks((prev) => {
                const newLinks = filterSelectedOrIgnoredItems(
                    response.landingPageLinks,
                    prev,
                    ignoredShopifyIds,
                )
                return [...prev, ...newLinks]
            })

            if (response.collectionInfo.failed > 0) {
                const successCount = response.landingPageLinks.length
                toast.warning(
                    `Added ${successCount} product${successCount !== 1 ? 's' : ''}, ${response.collectionInfo.failed} failed to import`,
                )
            } else {
                toast.success(
                    `Added all products from the ${collection.title} collection`,
                )
            }
        } catch (error) {
            console.error(error)
            toast.error('Unable to import collection. Please try again.')
        } finally {
            setIsAddingAll(false)
        }
    }

    const unselectedProductsCount = useMemo(() => {
        const selectedShopifyIds = selectedLinks.map((l) => l.shopifyId)
        return validProducts.filter(
            (product) => !selectedShopifyIds.includes(product.shopifyId),
        ).length
    }, [validProducts, selectedLinks])

    const unselectedProductsText = useMemo(() => {
        return unselectedProductsCount > 0
            ? unselectedProductsCount < validProducts.length
                ? `(${unselectedProductsCount} unselected)`
                : '(none selected)'
            : ''
    }, [unselectedProductsCount, validProducts.length])

    return (
        <Card className="hover:border-foreground/20 p-4 transition-all hover:shadow-sm">
            <div className="flex flex-col gap-2">
                <div className="flex flex-row gap-2">
                    <button
                        className="flex grow-0 cursor-pointer items-start gap-2"
                        onClick={toggleExpanded}
                        role="button"
                        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${collection.title} products`}
                        aria-expanded={isExpanded}
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                toggleExpanded()
                            }
                        }}
                    >
                        <div
                            className={cn(
                                'text-foreground flex items-center transition-transform duration-200',
                                isExpanded ? 'rotate-0' : '-rotate-90',
                            )}
                        >
                            <Icon
                                type="chevron-down"
                                color="currentColor"
                                width={20}
                                height={20}
                            />
                        </div>
                    </button>
                    <div className="flex grow flex-col items-start gap-1">
                        <div className="flex gap-1">
                            <Small className="font-semibold">
                                {collection.title}
                            </Small>
                        </div>
                        {collection.templateSuffix && (
                            <XSmall className="text-muted-foreground">
                                Template: {collection.templateSuffix}
                            </XSmall>
                        )}
                        <XSmall className="text-muted-foreground">
                            Shopify ID: {collection.shopifyId}
                        </XSmall>
                        <XSmall className="text-muted-foreground">
                            Products: {validProducts.length}{' '}
                            {unselectedProductsText}
                        </XSmall>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                        <Button
                            onClick={handleAddAll}
                            disabled={
                                isAddingAll || unselectedProductsCount === 0
                            }
                            size="sm"
                            variant="default"
                            aria-label="Add all remaining"
                        >
                            {isAddingAll ? (
                                <>
                                    <LoadingSpinner className="mr-2 size-4" />
                                    Adding...
                                </>
                            ) : (
                                'Add all'
                            )}
                        </Button>
                    </div>
                </div>
                {isExpanded && (
                    <div className="mt-4 border-t pt-4 border-border">
                        {validProducts.length === 0 ? (
                            <Muted>No products in this collection</Muted>
                        ) : (
                            <div className="divide-y overflow-hidden rounded-lg border divide-border border-border">
                                {validProducts.map((product) => {
                                    const isAlreadySelected =
                                        selectedLinks.some(
                                            (link) =>
                                                link.shopifyId ===
                                                product.shopifyId,
                                        )
                                    return (
                                        <AddShopifyProductThumbnail
                                            key={
                                                (
                                                    product as ProductWithVirtualInfo
                                                ).virtualProductId
                                                    ? `virtual-${
                                                          (
                                                              product as ProductWithVirtualInfo
                                                          ).virtualProductId
                                                      }`
                                                    : `regular-${product.shopifyId}`
                                            }
                                            product={product}
                                            onSelect={onSelect}
                                            isDisabled={
                                                isAlreadySelected || isAddingAll
                                            }
                                        />
                                    )
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Card>
    )
}

interface AddShopifyProductThumbnailProps {
    onSelect: (products: ProductWithVirtualInfo[]) => Promise<void>
    product: ProductWithVirtualInfo
    isDisabled?: boolean
}

function getProductDisplayShopifyId(product: ProductWithVirtualInfo): string {
    if (product.virtualProductId) {
        return product.defaultShopifyProductId ?? product.shopifyId
    }
    return product.shopifyId
}

function getLinkDisplayShopifyId(
    link: AdminLandingPageLink,
    virtualProductDefaultShopifyIdById: Map<number, string | null>,
): string {
    if (link.virtualProductId) {
        return (
            virtualProductDefaultShopifyIdById.get(link.virtualProductId) ??
            link.shopifyId
        )
    }
    return link.shopifyId
}

type ProductPickerOrderStatsProps = {
    quantityOrdered?: number | null
    lastOrderDate?: Date | string | null
    isVirtual?: boolean
}

const ProductPickerOrderStats: React.FC<ProductPickerOrderStatsProps> = ({
    quantityOrdered,
    lastOrderDate,
    isVirtual = false,
}) => {
    const ordersPast30DaysDisplay =
        quantityOrdered != null ? String(quantityOrdered) : '—'
    const lastOrderedDisplay = formatLastOrderedLabel(lastOrderDate)

    return (
        <div className="shrink-0 text-right text-xs text-muted-foreground">
            <div>
                Orders (past 30 days):{' '}
                <span className="font-medium text-foreground">
                    {ordersPast30DaysDisplay}
                </span>
            </div>
            <div className="mt-0.5">
                Last ordered:{' '}
                <span className="font-medium text-foreground">
                    {lastOrderedDisplay}
                </span>
            </div>
            {isVirtual && (
                <div className="mt-0.5 text-muted-foreground">
                    Virtual product
                </div>
            )}
        </div>
    )
}

type ProductPickerShopifyIdLineProps = {
    shopifyId: string
    isFeatured?: boolean
}

const ProductPickerShopifyIdLine: React.FC<ProductPickerShopifyIdLineProps> = ({
    shopifyId,
    isFeatured = false,
}) => (
    <XSmall className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-muted-foreground">
        <span className="break-all">Shopify ID: {shopifyId}</span>
        {isFeatured && (
            <Badge className="shrink-0 text-[10px] bg-black text-white hover:bg-black/80">
                Featured
            </Badge>
        )}
    </XSmall>
)

const ProductPickerTitle: React.FC<{ children: ReactNode }> = ({
    children,
}) => (
    <XSmall className="block break-words font-medium leading-snug text-foreground">
        {children}
    </XSmall>
)

type SelectedProductPickerRowProps = {
    link: AdminLandingPageLink
    displayShopifyId: string
    isFeatured?: boolean
    isDragOverlay?: boolean
    defaultVariantLine?: ReactNode
    onRemove?: (link: AdminLandingPageLink) => void
}

const SelectedProductPickerRow: React.FC<SelectedProductPickerRowProps> = ({
    link,
    displayShopifyId,
    isFeatured = false,
    isDragOverlay = false,
    defaultVariantLine,
    onRemove,
}) => {
    const isVirtual = !!link.virtualProductId

    return (
        <div
            className={cn(
                'flex items-start gap-3 p-3',
                isDragOverlay &&
                    'cursor-grabbing rounded-lg border border-primary/40 bg-card shadow-2xl ring-2 ring-primary/20',
            )}
        >
            <div
                className={cn(
                    'mt-0.5 flex shrink-0 items-center',
                    isDragOverlay ? 'text-primary' : 'text-muted-foreground',
                )}
                aria-hidden={isDragOverlay}
            >
                <Icon type="menu" color="currentColor" width={18} height={18} />
            </div>
            <div className="relative size-10 shrink-0 overflow-hidden rounded-md bg-muted">
                {link.imageUrl && (
                    <Image
                        src={link.imageUrl}
                        alt={link.title}
                        fill
                        className="object-cover"
                        sizes="40px"
                    />
                )}
            </div>
            <div className="min-w-0 flex-1">
                <ProductPickerTitle>{link.title}</ProductPickerTitle>
                <ProductPickerShopifyIdLine
                    shopifyId={displayShopifyId}
                    isFeatured={isFeatured}
                />
                {defaultVariantLine}
            </div>
            <ProductPickerOrderStats
                quantityOrdered={link.quantityOrdered}
                lastOrderDate={link.lastOrderDate}
                isVirtual={isVirtual}
            />
            {onRemove && !isDragOverlay && (
                <Button
                    size="sm"
                    variant="outline"
                    className="hover:bg-destructive/10 mt-0.5 shrink-0 border-destructive text-destructive hover:text-destructive"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation()
                        onRemove(link)
                    }}
                >
                    Remove
                </Button>
            )}
        </div>
    )
}

type VirtualProductAssociatedProductsProps = {
    attachedProducts: Array<{ id: string; name: string | null }>
    defaultShopifyProductId?: string | null
}

const VirtualProductAssociatedProducts: React.FC<
    VirtualProductAssociatedProductsProps
> = ({ attachedProducts, defaultShopifyProductId }) => (
    <div className="flex flex-col gap-1">
        {attachedProducts.map((attachedProduct) => {
            const isPrimary = defaultShopifyProductId === attachedProduct.id
            return (
                <div
                    key={attachedProduct.id}
                    className={cn(
                        'flex flex-col gap-0.5',
                        isPrimary &&
                            'rounded-md border border-primary/20 bg-primary/10 p-1',
                    )}
                >
                    <div className="flex items-center gap-1.5">
                        <XSmall
                            className={cn(
                                'font-medium',
                                isPrimary ? 'text-primary' : 'text-foreground',
                            )}
                        >
                            {attachedProduct.name || 'Product'}
                        </XSmall>
                        {isPrimary && (
                            <Badge
                                variant="default"
                                className="h-4 px-1 py-0 text-[9px] font-semibold bg-primary text-primary-foreground"
                            >
                                Primary
                            </Badge>
                        )}
                    </div>
                    <XSmall className="font-mono text-muted-foreground">
                        ID: {attachedProduct.id}
                    </XSmall>
                </div>
            )
        })}
    </div>
)

const AddShopifyProductThumbnail: React.FC<AddShopifyProductThumbnailProps> = ({
    onSelect,
    product,
    isDisabled = false,
}) => {
    const imageSrc = product.images[0]?.src || null
    const [isLoading, setIsLoading] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)
    const isVirtual = !!product.virtualProductId
    const attachedProducts = product.attachedProducts || []
    const defaultShopifyProductId = product.defaultShopifyProductId
    const displayShopifyId = getProductDisplayShopifyId(product)
    const hasAssociatedProducts = isVirtual && attachedProducts.length > 0

    const handleAdd = async () => {
        setIsLoading(true)
        try {
            await onSelect([product])
        } catch (e) {
            console.error(e)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="hover:bg-accent/50 transition-colors bg-card">
            <div className="flex items-start gap-3 p-3">
                <div className="relative size-10 shrink-0 overflow-hidden rounded-md bg-muted">
                    {imageSrc && (
                        <Image
                            alt={product.title || 'Product image'}
                            src={imageSrc}
                            fill
                            className="object-cover"
                            sizes="40px"
                        />
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <ProductPickerTitle>{product.title}</ProductPickerTitle>
                    <ProductPickerShopifyIdLine shopifyId={displayShopifyId} />
                    {hasAssociatedProducts && (
                        <button
                            type="button"
                            className="mt-0.5 flex items-center gap-1 text-xs transition-colors text-muted-foreground hover:text-foreground"
                            onClick={() => setIsExpanded((prev) => !prev)}
                            aria-expanded={isExpanded}
                        >
                            <span
                                className={cn(
                                    'flex items-center transition-transform duration-200',
                                    isExpanded ? 'rotate-0' : '-rotate-90',
                                )}
                            >
                                <Icon
                                    type="chevron-down"
                                    color="currentColor"
                                    width={14}
                                    height={14}
                                />
                            </span>
                            {attachedProducts.length} associated product
                            {attachedProducts.length === 1 ? '' : 's'}
                        </button>
                    )}
                </div>
                <ProductPickerOrderStats
                    quantityOrdered={product.quantityOrdered}
                    lastOrderDate={product.lastOrderDate}
                    isVirtual={isVirtual}
                />
                <Button
                    onClick={handleAdd}
                    disabled={isLoading || isDisabled}
                    size="sm"
                    variant="default"
                    className="mt-0.5 shrink-0"
                >
                    {isLoading ? (
                        <>
                            <LoadingSpinner className="mr-2 size-4" />
                            Adding...
                        </>
                    ) : (
                        'Add'
                    )}
                </Button>
            </div>
            {hasAssociatedProducts && isExpanded && (
                <div className="bg-muted/30 border-t py-1.5 pl-14 pr-2 border-border">
                    <VirtualProductAssociatedProducts
                        attachedProducts={attachedProducts}
                        defaultShopifyProductId={defaultShopifyProductId}
                    />
                </div>
            )}
        </div>
    )
}

interface ReorderLinkThumbnailProps {
    link: AdminLandingPageLink
    displayShopifyId: string
    onRemove: (link: AdminLandingPageLink) => void
    onApplyLinkUpdate: (
        linkHash: string,
        nextLink: AdminLandingPageLink,
    ) => void
    showStorefrontDefaultVariantUI?: boolean
    isFeatured?: boolean
}

const ReorderLinkThumbnailComponent: React.FC<ReorderLinkThumbnailProps> = ({
    link,
    displayShopifyId,
    onRemove,
    onApplyLinkUpdate,
    showStorefrontDefaultVariantUI = false,
    isFeatured = false,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: link.linkHash })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    }

    const isVirtual = !!link.virtualProductId

    const defaultVariantLine = useMemo(() => {
        if (!showStorefrontDefaultVariantUI) return null
        if (!shouldShowSelectedProductDefaultVariantLine(link)) return null
        return (
            <SelectedProductDefaultVariantLine
                link={link}
                onApplyLinkUpdate={onApplyLinkUpdate}
            />
        )
    }, [link, onApplyLinkUpdate, showStorefrontDefaultVariantUI])

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className={cn(
                'cursor-grab bg-card transition-colors active:cursor-grabbing',
                isDragging ? 'opacity-40' : 'hover:bg-accent/50',
            )}
            aria-label={`Drag to reorder ${link.title}${isFeatured ? ' (featured product)' : ''}${isVirtual ? ' (virtual product)' : ''}`}
            role="button"
            tabIndex={0}
        >
            <SelectedProductPickerRow
                link={link}
                displayShopifyId={displayShopifyId}
                isFeatured={isFeatured}
                defaultVariantLine={defaultVariantLine}
                onRemove={onRemove}
            />
        </div>
    )
}

const ReorderLinkThumbnail = memo(ReorderLinkThumbnailComponent)
